import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage, intlShape } from 'react-intl';
import { CancelButton, ConfirmButton } from 'components/ui/Button';

import { StyledColumn } from '../../AfterSalesWarning/AfterSalesWarning.styles';
import { Row } from 'components/shared/Layout';

import Modal from 'components/ui/Modal';
import { ActionButton } from 'components/ui/Button';
import { WRITE_OFF_REASONS } from 'constants/reasons';
import { bookingShape } from 'constants/propTypes';
import { ButtonWrapper } from '../../../../../components/shared/ForcedCancellationModal/ForcedCancellationModal.styles';

import { StyledDropdown, StyledLabel, StyledTextArea } from './ExchangeCtaOverride.styles';

import {
  hasSegmentDepartedMoreThan60DaysAgo,
  getSegmentsForItemRefs,
  selectedProductsHaveDifferentProductFamily,
  getProductFamiliesPerPassengersInMixedPF,
  hasDBTicketsSelected
} from '../AfterSalesCtas.utils';
import { CtaButton } from '../AfterSalesCtas.styles';
import { IconContainer, SubtitleContainer, TitleContainer } from '../WarningCta/WarningCta.styles';
import { WarningIcon } from '../../../../../components/ui/AlertBox/AlertBox.styles';
import WarningCtaMultiplePF from '../WarningCta/WarningCtaMultiplePF';

const ExchangeCtaOverride = ({
  isExchangeDisabled,
  label,
  onClick,
  intl: { formatMessage },
  selectedItemRefs,
  booking,
  isPackageBooking,
  exchangingSubsetOfPaxsInPALBooking,
  hasInsurance = false,
  showSNCBWarning = false
}) => {
  const segments = booking && Array.from(getSegmentsForItemRefs(booking, selectedItemRefs));
  const shouldShowWarning = hasSegmentDepartedMoreThan60DaysAgo(segments);
  const hasDBTickets = hasDBTicketsSelected({ segments });
  const hasMoreThanOneProductFamily = booking && selectedProductsHaveDifferentProductFamily({ selectedItemRefs, booking });
  const productFamiliesPerPassengersInMixedPF = getProductFamiliesPerPassengersInMixedPF({
    booking,
    selectedItemRefs,
    hasMoreThanOneProductFamily
  });
  const [showWarning, setShowWarning] = useState(shouldShowWarning);
  const [isOpen, setOpen] = useState(false);
  const [reason, setReason] = useState(null);
  const [reasonLabel, setReasonLabel] = useState(null);
  const [reasonInfo, setReasonInfo] = useState('');

  const exchangeReasonAdditionalInfoRequired = () => {
    const exchangeReason = WRITE_OFF_REASONS.find((r) => r.code === reason);

    if (!exchangeReason.requiresAdditional) {
      return false;
    }
    if (exchangeReason.requiresAdditional && !!reasonInfo) {
      return false;
    }

    return true;
  };

  const isExchangeDisabledOverride = isExchangeDisabled || !reason || exchangeReasonAdditionalInfoRequired();

  const handleClickOverride = () => {
    setOpen(!isOpen);
  };

  const handleClose = () => {
    setOpen(!isOpen);
    setReason(null);
    setReasonLabel(null);
    setReasonInfo(null);
  };

  const handleReasonChange = (e) => {
    const [value, reasonLabel] = e.target.value.split(`_`);
    setReason(value);
    setReasonLabel(reasonLabel);
  };

  const handleReasonInfoChange = (e) => {
    setReasonInfo(e.target.value);
  };

  const handleExchangeClick = () => {
    onClick(reason, reasonLabel, reasonInfo, exchangingSubsetOfPaxsInPALBooking);
  };

  return (
    <>
      <CtaButton
        type="button"
        data-cy="exchange-cta-override"
        data-id="exchange-cta-override"
        disabled={isExchangeDisabled}
        label={label}
        onClick={() => {
          handleClickOverride();
          setShowWarning(shouldShowWarning);
        }}
      >
        {label}
      </CtaButton>

      {showWarning ? (
        <Modal title={<FormattedMessage id="outsideNormalReasonsForOverride.cancellationWarning" />} isOpen={isOpen} onClose={handleClose}>
          <div>
            {isPackageBooking && (
              <StyledLabel danger>
                <FormattedMessage id="exchanges.packageBookingSelected" /> -{' '}
                <FormattedMessage id="exchanges.packageBookingSameDepartureDate" />
              </StyledLabel>
            )}
            {showSNCBWarning && (
              <StyledLabel danger>
                <FormattedMessage id="sncbStationBookingSelected" /> - <FormattedMessage id="printWarning.SNCB" />
              </StyledLabel>
            )}
            {exchangingSubsetOfPaxsInPALBooking && (
              <StyledLabel danger>
                <FormattedMessage id="aftersalesWarning.subsetPalsTitle" /> - <FormattedMessage id="aftersalesWarning.subsetPalsText" />
              </StyledLabel>
            )}
            {hasInsurance && (
              <StyledLabel danger>
                <FormattedMessage id="exchanges.insuranceWarning.title" /> - <FormattedMessage id="exchanges.insuranceWarning" />
              </StyledLabel>
            )}
            <p>
              <FormattedMessage id="outsideNormalReasonsForOverride.overridingRulesWarning" />
            </p>
            <p>
              <FormattedMessage id="outsideNormalReasonsForOverride.overridingRulesWarningProceed" />
            </p>
          </div>

          <ButtonWrapper>
            <ActionButton
              type="button"
              data-cy="forced-cancellation-warning-confirm"
              data-testid="abandon-confirm-button-warning"
              onClick={() => setShowWarning(false)}
            >
              <FormattedMessage id="outsideNormalReasonsForOverride.confirmCancellationWarning" />
            </ActionButton>

            <ActionButton
              type="button"
              data-cy="forced-cancellation-warning-abandon"
              data-testid="abandon-button-warning"
              onClick={handleClose}
            >
              <FormattedMessage id="outsideNormalReasonsForOverride.abandonCancellationWarning" />
            </ActionButton>
          </ButtonWrapper>
        </Modal>
      ) : hasMoreThanOneProductFamily ? (
        <Modal
          isOpen={isOpen}
          onClose={handleClose}
          title={
            <TitleContainer>
              <IconContainer>
                <WarningIcon />
              </IconContainer>
              {<FormattedMessage id="afterSales.warning" />}
            </TitleContainer>
          }
          subtitle={
            <SubtitleContainer>
              <FormattedMessage id={'exchanges.productFamilyWarning'} />
              {(productFamiliesPerPassengersInMixedPF?.outbound?.length > 0 ||
                productFamiliesPerPassengersInMixedPF?.inbound?.length > 0) && (
                <WarningCtaMultiplePF productFamiliesPerPassengersInMixedPF={productFamiliesPerPassengersInMixedPF} />
              )}
            </SubtitleContainer>
          }
        >
          {hasInsurance && (
            <StyledLabel danger>
              <FormattedMessage id="exchanges.insuranceWarning.title" /> - <FormattedMessage id="exchanges.insuranceWarning" />
            </StyledLabel>
          )}
        </Modal>
      ) : (
        <Modal
          isOpen={isOpen}
          onClose={handleClose}
          title={formatMessage({ id: 'exchangeCtaOverride.title' })}
          subtitle={
            hasDBTickets ? formatMessage({ id: 'exchangeCtaOverride.subtitleDB' }) : formatMessage({ id: 'exchangeCtaOverride.subtitle' })
          }
        >
          {hasDBTickets ? (
            <FormattedMessage id="db.overrideNotPossible" />
          ) : (
            <>
              <StyledDropdown name="reasonCode" data-cy="write-off-reason" onChange={handleReasonChange}>
                <option value="">{formatMessage({ id: 'payments.dropdownDefault' })}</option>
                {WRITE_OFF_REASONS.map(({ code, label: reasonLabel }) => (
                  <option key={code} value={`${code}_${reasonLabel}`}>
                    {reasonLabel}
                  </option>
                ))}
              </StyledDropdown>

              <div>
                <StyledLabel>
                  <FormattedMessage id="payments.writeOffAdditionalInfo" />
                </StyledLabel>
                <StyledTextArea
                  name="additionalInformation"
                  value={reasonInfo}
                  onChange={handleReasonInfoChange}
                  rows="3"
                  cols="40"
                  data-cy="write-off-refund-additional-information"
                  maxLength={37}
                />
              </div>
            </>
          )}

          {isPackageBooking && (
            <StyledLabel>
              <FormattedMessage id="exchanges.packageBookingSelected" /> - <FormattedMessage id="exchanges.packageBookingContinue" />{' '}
              <FormattedMessage id="exchanges.packageBookingSameDepartureDate" />
            </StyledLabel>
          )}
          {hasInsurance && (
            <StyledLabel>
              <FormattedMessage id="exchanges.insuranceWarning.title" /> - <FormattedMessage id="exchanges.insuranceWarning" />
            </StyledLabel>
          )}

          {exchangingSubsetOfPaxsInPALBooking && (
            <StyledLabel danger>
              <FormattedMessage id="aftersalesWarning.subsetPalsTitle" /> - <FormattedMessage id="aftersalesWarning.subsetPalsText" />
            </StyledLabel>
          )}

          <Row>
            <StyledColumn span={3}>
              <ConfirmButton
                type="button"
                data-cy="exchange-cta-override-in-modal"
                data-id="exchange-cta-override-in-modal"
                disabled={isExchangeDisabledOverride || hasDBTickets}
                label={label}
                onClick={handleExchangeClick}
              >
                {label}
              </ConfirmButton>
            </StyledColumn>
            <StyledColumn span={3}>
              <CancelButton type="button" data-cy="abandon-exchange" data-testid="abandon-exchange" onClick={handleClose}>
                <FormattedMessage id="exchanges.adandon" />
              </CancelButton>
            </StyledColumn>
          </Row>
        </Modal>
      )}
    </>
  );
};

ExchangeCtaOverride.propTypes = {
  isExchangeDisabled: PropTypes.bool,
  label: PropTypes.node,
  onClick: PropTypes.func,
  intl: intlShape,
  booking: bookingShape,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string),
  isPackageBooking: PropTypes.bool,
  exchangingSubsetOfPaxsInPALBooking: PropTypes.bool,
  hasInsurance: PropTypes.bool,
  showSNCBWarning: PropTypes.bool
};

export default injectIntl(ExchangeCtaOverride);
